import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { api } from "../lib/api";
import { toast } from "sonner";
import { UserCheck, UserX, Clock, Building2, MapPin, Loader2 } from "lucide-react";
import { formatDateFr } from "../lib/dateFormat";

/**
 * Modal de réaffectation 1-clic d'une intervention vacante.
 *
 * Props :
 * - open, onOpenChange
 * - vacancy: objet issu de /department-agenda/vacant-to-fill OU de /vacancies :
 *   {
 *     vacancy_id | id, date, start_time, end_time,
 *     brand_id, brand_name, site_name, poste,
 *     absent_collaborateur_id, absent_collaborateur_name, origin,
 *   }
 * - onReplaced() : callback après succès.
 */
export default function ReassignVacancyDialog({ open, onOpenChange, vacancy, onReplaced }) {
  const vacancyId = vacancy?.vacancy_id || vacancy?.id;
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !vacancyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/vacancies/${vacancyId}/eligible-collaborators`);
        if (cancelled) return;
        setCandidates(data || []);
        setSelectedId("");
      } catch {
        if (!cancelled) setCandidates([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, vacancyId]);

  const handleSubmit = async () => {
    if (!selectedId || !vacancyId) return;
    setSubmitting(true);
    try {
      await api.post(`/vacancies/${vacancyId}/replace`, { collaborateur_id: selectedId });
      toast.success("Vacance réaffectée avec succès");
      onOpenChange(false);
      onReplaced?.();
      // Un nouveau shift est créé en base — synchroniser les widgets shifts
      window.dispatchEvent(new CustomEvent("plania:shifts-updated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de réaffecter");
    } finally {
      setSubmitting(false);
    }
  };

  if (!vacancy) return null;

  const origin = vacancy.origin || "absence";
  const originLabel = origin === "unavailability" ? "Indisponibilité" : "Absence";
  const absentName = vacancy.absent_collaborateur_name || "—";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="reassign-vacancy-dialog">
        <DialogHeader>
          <DialogTitle>Réaffecter le shift vacant</DialogTitle>
          <DialogDescription>
            Sélectionnez un collaborateur disponible. Un nouveau shift de remplacement sera créé — le shift d&apos;origine reste marqué {originLabel.toLowerCase()} pour la traçabilité.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 space-y-1.5" data-testid="reassign-vacancy-summary">
            <p className="text-sm font-medium text-zinc-900">
              {vacancy.poste || "Shift à combler"}
            </p>
            <p className="text-[11px] text-red-700 inline-flex items-center gap-1.5">
              <UserX size={11} />
              {originLabel} : <span className="font-medium">{absentName}</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] font-mono text-zinc-600">
              <p className="inline-flex items-center gap-1.5">
                <Clock size={11} className="text-zinc-400" />
                {formatDateFr(vacancy.date)} · {vacancy.start_time} → {vacancy.end_time}
              </p>
              <p className="inline-flex items-center gap-1.5">
                <Building2 size={11} className="text-zinc-400" /> {vacancy.brand_name || "—"}
              </p>
              {vacancy.site_name && (
                <p className="inline-flex items-center gap-1.5 sm:col-span-2">
                  <MapPin size={11} className="text-zinc-400" /> {vacancy.site_name}
                </p>
              )}
            </div>
          </div>

          <div>
            <Label className="label-uppercase mb-2 block">Collaborateurs disponibles</Label>
            {loading ? (
              <p className="text-sm text-zinc-500 font-mono py-3">Chargement…</p>
            ) : candidates.length === 0 ? (
              <p className="text-sm text-zinc-500 font-mono py-3 italic" data-testid="reassign-no-candidates">
                Aucun collaborateur disponible sur ce créneau dans ce département.
              </p>
            ) : (
              <div className="border border-zinc-200 rounded-sm max-h-64 overflow-y-auto">
                {candidates.map((c) => (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 px-3 py-2 border-b border-zinc-100 cursor-pointer hover:bg-zinc-50 ${
                      selectedId === c.id ? "bg-blue-50" : "bg-white"
                    }`}
                    data-testid={`reassign-candidate-${c.id}`}
                  >
                    <input
                      type="radio"
                      name="reassign-candidate"
                      value={c.id}
                      checked={selectedId === c.id}
                      onChange={(e) => setSelectedId(e.target.value)}
                      className="h-3.5 w-3.5"
                    />
                    <span className="flex-1 text-sm">
                      <span className="font-medium text-zinc-900">{c.name}</span>
                      <span className="text-zinc-400 text-[11px] font-mono ml-2">{c.role}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="reassign-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedId || submitting}
            data-testid="reassign-submit"
            className="bg-zinc-900 hover:bg-zinc-800 text-white"
          >
            {submitting ? (
              <Loader2 size={14} className="mr-1.5 animate-spin" />
            ) : (
              <UserCheck size={14} className="mr-1.5" />
            )}
            Affecter le collaborateur
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
